import React, { useEffect, useState } from 'react';
import Footer from '../../components/Footer';
import {
  Emphasis,
  InnerCard,
  InnerContainer,
  InnerSection,
  PageTitle,
  Paragraph,
  SectionTitle,
  TextLink,
} from '../../components/InnerPageLayout';
import JsonData from '../../data/data.json';
import { SiteData } from '../../types/siteDataTypes';
import InnerPageHeader from '../../components/InnerPageHeader';

const Terms: React.FC = () => {
  const [siteData, setSiteData] = useState<SiteData | null>(null);

  useEffect(() => {
    setSiteData(JsonData as SiteData);
  }, []);
  
  const APP_NAME = 'Chot Study';

  return (
    <>
      {siteData ? (
        <>
          <InnerPageHeader data={siteData.ChotStudyPrivacyHeader} />
          <InnerSection>
            <InnerContainer>
              <InnerCard>
                <PageTitle>Terms of Service</PageTitle>

                <Paragraph>Effective Date: {siteData.AppsData.effectiveDate}</Paragraph>

                <SectionTitle>1. Acceptance of Terms</SectionTitle>
                <Paragraph>
                  By accessing or using the Chot Study app ("the App"), operated by Chots AI
                  ("we," "our," or "us"), you agree to comply with and be bound by these Terms of
                  Service ("Terms"). These Terms constitute a legally binding agreement between
                  you and Chots AI. If you do not agree to these Terms, you must not use the App.
                </Paragraph>

                <SectionTitle>2. Use of the App</SectionTitle>
                <Paragraph>
                  <Emphasis>Eligibility:</Emphasis> The App is intended for users who are at least
                  16 years old. By using the App, you represent and warrant that you are at least
                  16 years of age and have the legal capacity to enter into this agreement.
                  {`\n\n`}
                  <Emphasis>Account Registration:</Emphasis> To access certain features of the App,
                  you must create an account. You agree to provide accurate, current, and complete
                  information during the registration process and to update such information as
                  necessary to maintain its accuracy. You are responsible for safeguarding your
                  account credentials and for all activities that occur under your account. You
                  agree to notify us immediately of any unauthorized use of your account.
                  {`\n\n`}
                  <Emphasis>Prohibited Activities:</Emphasis> You agree not to engage in any
                  activities that could harm the App, its users, or third parties. This includes,
                  but is not limited to:{`\n \u25CF`} Uploading or transmitting viruses, malware, or
                  any other harmful code.
                  {`\n \u25CF`} Engaging in fraudulent, deceptive, or misleading practices.
                  {`\n \u25CF`} Attempting to gain unauthorized access to the App, its systems, or
                  other users' accounts.
                  {`\n \u25CF`} Using the App for any illegal activities, including spamming,
                  harassment, or infringement of intellectual property rights.
                  {`\n \u25CF`} Reselling, licensing, or otherwise commercially exploiting the App
                  without our prior written consent.
                </Paragraph>

                <SectionTitle>3. Subscription and Payments</SectionTitle>
                <Paragraph>
                  <Emphasis>Subscription Plans:</Emphasis> The App offers subscription-based access
                  to certain features and services. By subscribing, you agree to pay the applicable
                  subscription fees as described in the App. Subscription fees are billed on a
                  recurring basis (e.g., monthly or annually) until canceled. You can manage or
                  cancel your subscription through your account settings in the App or via the
                  platform (e.g., Google Play Store or Apple App Store) where you purchased the
                  subscription.
                  {`\n\n`}
                  <Emphasis>Payment Processing:</Emphasis> All payments made through the App are
                  processed by third-party payment processors. By making a payment, you agree to
                  the payment processor's terms of service and privacy policy. We are not
                  responsible for any errors or issues that arise from the payment process.
                </Paragraph>

                <SectionTitle>4. Content Ownership and User Data</SectionTitle>
                <Paragraph>
                  <Emphasis>User Data:</Emphasis> You retain ownership of all content and data you
                  provide through the App, including chat messages and personal context. Your
                  personal data is protected with end-to-end encryption. By using the App, you
                  grant us a worldwide, non-exclusive, royalty-free, transferable license to use,
                  process, store, and display your data for the purpose of providing and improving
                  the App's services. This license will terminate when you delete your data or
                  terminate your account, except for data that has been anonymized or aggregated.
                  {`\n\n`}
                  <Emphasis>Data Retention:</Emphasis> We retain your encrypted chat messages and
                  personal context as described in our Privacy Policy. You may request the deletion
                  of your data at any time, subject to the terms of our Privacy Policy. We reserve
                  the right to retain certain data as required by law or for legitimate business
                  purposes.
                  {`\n\n`}
                  <Emphasis>Feedback:</Emphasis> If you provide feedback or suggestions regarding the
                  App, you grant us a perpetual, irrevocable, royalty-free, and fully sublicensable
                  right to use, modify, distribute, and incorporate such feedback into the App or
                  any other products or services without any obligation to you.
                </Paragraph>

                <SectionTitle>5. Intellectual Property Rights</SectionTitle>
                <Paragraph>
                  <Emphasis>Ownership:</Emphasis> All intellectual property rights in the App,
                  including but not limited to its content, features, design, software, and
                  trademarks, are owned by or licensed to Chots AI. Except as expressly permitted
                  by these Terms, you may not use, reproduce, distribute, or create derivative works
                  based on the App without our prior written consent.
                  {`\n\n`}
                  <Emphasis>Limited License:</Emphasis> We grant you a limited, non-exclusive,
                  non-transferable, revocable license to use the App for your personal use, subject
                  to these Terms. This license does not grant you any ownership rights in the App or
                  its content.
                </Paragraph>

                <SectionTitle>6. AI Assistants and Health Disclaimers</SectionTitle>
                <Paragraph>
                  <Emphasis>AI Technology:</Emphasis> The App uses a semantic router to
                  intelligently select from multiple large language models (LLMs) including OpenAI,
                  Google, Meta, and others. Each message is routed to the most appropriate model
                  based on context and requirements.
                  {`\n\n`}
                  <Emphasis>Not Professional Advice:</Emphasis> The AI assistants, including but not
                  limited to the Wellness Guide, Health & Symptom Guide, Psychology Companion, and
                  other personal assistants, are for informational and entertainment purposes only.
                  They do not provide medical, psychological, legal, financial, or any other form of
                  professional advice.
                  {`\n\n`}
                  <Emphasis>Health & Wellness Disclaimer:</Emphasis> Do not use this App as a
                  substitute for professional medical or mental health care. Always consult
                  qualified healthcare providers for medical concerns, mental health issues, or
                  before making any health-related decisions.
                  {`\n\n`}
                  <Emphasis>Emergency Situations:</Emphasis> This App is not designed for emergency
                  situations. If you are experiencing a medical or mental health emergency, contact
                  emergency services immediately.
                  {`\n\n`}
                  <Emphasis>User Responsibility:</Emphasis> You are solely responsible for any
                  decisions or actions you take based on information provided by the AI assistants.
                  We strongly encourage you to verify any important information with appropriate
                  professionals.
                </Paragraph>

                <SectionTitle>7. Limitations of Liability</SectionTitle>
                <Paragraph>
                  <Emphasis>LLM Disclaimer:</Emphasis> While we strive to provide accurate and
                  helpful information through our AI assistants, we do not guarantee the accuracy,
                  completeness, or reliability of any content generated. You acknowledge that the
                  LLMs may produce incorrect, misleading, or otherwise inappropriate responses, and
                  you agree that we shall not be liable for any harm or damages resulting from your
                  use of the App.
                  {`\n\n`}
                  <Emphasis>General Disclaimer:</Emphasis> The App is provided on an "as is" and "as
                  available" basis. To the fullest extent permitted by law, we disclaim all
                  warranties, express or implied, including but not limited to implied warranties
                  of merchantability, fitness for a particular purpose, and non-infringement. We do
                  not warrant that the App will be uninterrupted, secure, or error-free, or that
                  any defects or errors will be corrected.
                  {`\n\n`}
                  <Emphasis>Limitation of Liability:</Emphasis> To the maximum extent permitted by
                  law, Chots AI, its affiliates, directors, employees, agents, and licensors shall
                  not be liable for any indirect, incidental, special, consequential, or punitive
                  damages, including but not limited to loss of profits, data, or use, arising out
                  of or in connection with your use of or inability to use the App. Our total
                  liability to you for any damages, losses, or causes of action shall not exceed the
                  amount you have paid to us in the past six (6) months, or fifty dollars ($50),
                  whichever is greater.
                </Paragraph>

                <SectionTitle>8. Indemnification</SectionTitle>
                <Paragraph>
                  You agree to indemnify, defend, and hold harmless Chots AI, its affiliates,
                  directors, employees, agents, and licensors from and against any claims,
                  liabilities, damages, losses, and expenses, including reasonable attorneys' fees
                  and costs, arising out of or in any way connected with your use of the App, your
                  violation of these Terms, or your violation of any rights of another.
                </Paragraph>

                <SectionTitle>9. Termination and Suspension</SectionTitle>
                <Paragraph>
                  <Emphasis>Termination by User:</Emphasis> You may terminate your account at any
                  time by following the instructions in the App. Upon termination, your right to
                  use the App will cease immediately, and all data associated with your account
                  will be deleted in accordance with our Privacy Policy.
                  {`\n\n`}
                  <Emphasis>Termination by Us:</Emphasis> We reserve the right to suspend or
                  terminate your account and access to the App at our sole discretion, without
                  notice, for any violation of these Terms, any applicable law, or for any other
                  reason deemed appropriate by us, including but not limited to inactivity or
                  non-payment of fees.
                  {`\n\n`}
                  <Emphasis>Effect of Termination:</Emphasis> Upon termination of your account, we
                  will delete your data in accordance with our Privacy Policy. Any provisions of
                  these Terms that by their nature should survive termination (e.g., limitations of
                  liability, indemnification, intellectual property rights) shall survive the
                  termination of your account or these Terms.
                </Paragraph>

                <SectionTitle>10. Changes to These Terms</SectionTitle>
                <Paragraph>
                  We reserve the right to modify or update these Terms at any time in our sole
                  discretion. When we make changes, we will update the "Effective Date" at the top
                  of this document and provide notice to you through the App or by other means. Your
                  continued use of the App after any such changes constitutes your acceptance of the
                  revised Terms.
                </Paragraph>

                <SectionTitle>11. Governing Law and Dispute Resolution</SectionTitle>
                <Paragraph>
                  These Terms and any disputes arising out of or relating to them shall be governed
                  by and construed in accordance with the laws of the State of Delaware, without
                  regard to its conflict of law principles. Any legal action or proceeding arising
                  under these Terms shall be brought exclusively in the courts located in Delaware,
                  and you hereby consent to the personal jurisdiction and venue of such courts. You
                  agree to waive any right to a jury trial in any such action or proceeding.
                </Paragraph>

                <SectionTitle>12. Contact Information</SectionTitle>
                <Paragraph>
                  If you have any questions or concerns about these Terms, please contact us at{' '}
                  <TextLink
                    href={`mailto:${siteData.AppsData.customerSupportEmail}?subject=Regarding ${APP_NAME}'s Terms Of Service`}
                  >
                    {siteData.AppsData.customerSupportEmail}
                  </TextLink>
                  .
                </Paragraph>
              </InnerCard>
            </InnerContainer>
          </InnerSection>
          <Footer />
        </>
      ) : (
        <p>Loading...</p>
      )}
    </>
  );
};

export default Terms;
